import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Link,
  Grid,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import {
  useCN10Report,
  type CN10ReportResponse,
  type CN10DataStructure,
  type CN10FileData,
  type CN10ReportItem,
} from './hooks';
import { CN10Table } from './components';
import {
  MainContent,
  ReportContainer,
  SectionContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  ActionButton,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
} from './CN10Report.styled';

// Utility function to compare year-quarter combinations
const compareYearQuarter = (
  year1: string,
  quarter1: string,
  year2: string,
  quarter2: string
): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));

  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

interface MailCategoryState {
  aircraftPriority: boolean;
  surfacePriority: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// Memoized Results Section Component for CN10
const ResultsSection = React.memo<{
  htmlResults: CN10DataStructure;
  direction: string;
}>(({ htmlResults, direction }) => {
  return (
    <Box sx={{ mt: 6 }}>
      {/* Main Results Title */}
      <Box
        sx={{
          borderBottom: '2px solid #E1E8ED',
          pb: 2,
          mb: 4,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: '#063142',
            fontSize: '24px',
            letterSpacing: '-0.5px',
          }}
        >
          Report Results
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: '#666',
            mt: 0.5,
            fontSize: '14px',
          }}
        >
          Generated CN10 report data tables
        </Typography>
      </Box>

      {htmlResults.map((fileData: CN10FileData, fileIndex: number) => (
        <FileDataSection
          key={`${fileData.filename}-${fileIndex}`}
          fileData={fileData}
          fileIndex={fileIndex}
          direction={direction}
        />
      ))}
    </Box>
  );
});

// Memoized File Data Section Component
const FileDataSection = React.memo<{
  fileData: CN10FileData;
  fileIndex: number;
  direction: string;
}>(({ fileData, fileIndex, direction }) => {
  return (
    <Box
      sx={{
        mb: 6,
        p: 3,
        border: '1px solid #E1E8ED',
        borderRadius: '8px',
        backgroundColor: '#FAFBFC',
      }}
    >
      {/* File Header */}
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: '1px solid #E1E8ED',
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            color: '#063142',
            fontSize: '20px',
            letterSpacing: '-0.25px',
            mb: 1,
          }}
        >
          Operator: {fileData.designatedOperator} | Mail Category: {fileData.mailCategory}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: '#6C757D',
            fontSize: '12px',
            fontFamily: 'monospace',
            backgroundColor: '#FFFFFF',
            padding: '4px 8px',
            borderRadius: '3px',
            border: '1px solid #DEE2E6',
            display: 'inline-block',
          }}
        >
          📄 {fileData.filename}
        </Typography>
      </Box>

      {fileData.reportItems.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: '6px',
            border: '1px dashed #D1D9E0',
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              color: '#666',
              fontSize: '14px',
            }}
          >
            No data available for this file
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '6px',
            border: '1px solid #E1E8ED',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CN10Table
            data={fileData.reportItems}
            filename={fileData.filename}
            designatedOperator={fileData.designatedOperator}
            mailCategory={fileData.mailCategory}
            totalData={fileData.total}
            isLoading={false}
            rowBackgroundColor={
              direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'
            }
          />
        </Box>
      )}
    </Box>
  );
});

function CN10Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ppoValue, setPpoValue] = useState('All');
  const [ppoFilter, setPpoFilter] = useState('Inbound');
  const [fromYear, setFromYear] = useState('');
  const [fromQuarter, setFromQuarter] = useState('');
  const [toYear, setToYear] = useState('');
  const [toQuarter, setToQuarter] = useState('');

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<CN10DataStructure | null>(null);
  const [showNoData, setShowNoData] = useState<boolean>(false);

  // Snackbar state for error handling
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success'
  });

  // Ref to track if we've already synced initial URL parameters
  const hasSyncedUrlParams = useRef(false);

  // Get effective operators and cascading logic for e09 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e09', { fromYear, toYear, selectedPpo: ppoValue === 'All' ? undefined : ppoValue });

  // CN10 Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    reset: resetReportError,
  } = useCN10Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue && ppoValue !== 'All' ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach((quarter) => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = useCallback(() => {
    if (fromYear && fromQuarter) {
      return `${fromYear}-${fromQuarter}`;
    }
    return '';
  }, [fromYear, fromQuarter]);

  const getToValue = useCallback(() => {
    if (toYear && toQuarter) {
      return `${toYear}-${toQuarter}`;
    }
    return '';
  }, [toYear, toQuarter]);

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            setSnackbar({
              open: true,
              message: 'From date cannot be later than To date. Please select a valid date range.',
              severity: 'warning'
            });
            return; // Don't update the state if invalid
          }
        }
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find((opt) => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            setSnackbar({
              open: true,
              message: 'To date cannot be earlier than From date. Please select a valid date range.',
              severity: 'warning'
            });
            return; // Don't update the state if invalid
          }
        }
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Mail Category state
  const [mailCategory, setMailCategory] = useState<MailCategoryState>({
    aircraftPriority: true,
    surfacePriority: true,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e09');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = (value: string) => {
    setPpoValue(value);
  };

  // Handle year selection change with cascading behavior
  const handleYearChange = (value: string) => {
    // Only clear DO selection if the currently selected DO is not available for the new year
    if (fromYear !== value && ppoValue && ppoValue !== 'All') {
      // Check if the current DO is available for the new year
      const relationsForNewYear = allRelationsData?.byYear?.[value];
      const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === ppoValue &&
        relation.modules.some(module => 
          module.name === 'e09' && module.enabled === true
        )
      );
      
      if (!isCurrentDOAvailableForNewYear) {
        setPpoValue('All');
      }
    }
    setFromYear(value);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleMailCategoryChange =
    (field: keyof MailCategoryState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setMailCategory((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleSelectAllMailCategory = () => {
    const allSelected = mailCategory.aircraftPriority && mailCategory.surfacePriority;

    if (allSelected) {
      // Deselect all
      setMailCategory({
        aircraftPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setMailCategory({
        aircraftPriority: true,
        surfacePriority: true,
      });
    }
  };

  // Map checkbox states to API parameter values
  const getMailCategoryValues = useCallback((): string[] => {
    const values: string[] = [];
    if (mailCategory.aircraftPriority) values.push('A');
    if (mailCategory.surfacePriority) values.push('D');
    return values;
  }, [mailCategory.aircraftPriority, mailCategory.surfacePriority]);

  const handleReset = () => {
    setPpoValue('All');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setMailCategory({
      aircraftPriority: true,
      surfacePriority: true,
    });
    setShowNoData(false);
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = useCallback(() => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    newParams.append('ppo', ppoValue || 'All');

    // Map direction filter to type
    newParams.append('type', ppoFilter.toLowerCase());

    // Map from year if available
    if (fromYear) {
      newParams.append('year', fromYear);
    }

    // Add custom CN10Report-specific filters
    const mailCategories = getMailCategoryValues();

    if (mailCategories.length > 0) {
      newParams.append('mailCategories', mailCategories.join(','));
    }

    if (fromYear && fromQuarter) {
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    setSearchParams(newParams);
  }, [
    ppoValue,
    ppoFilter,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    getMailCategoryValues,
    setSearchParams,
  ]);

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    const mailCategories = getMailCategoryValues();

    if (mailCategories.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one Mail Category',
        severity: 'warning'
      });
      return;
    }

    // Prepare API parameters
    const params = {
      mailCategories: mailCategories,
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      ...(fromYear &&
        fromQuarter && {
          fromYear: parseInt(fromYear),
          fromQuarter: parseInt(fromQuarter.replace('Q', '')),
        }),
      ...(toYear &&
        toQuarter && {
          toYear: parseInt(toYear),
          toQuarter: parseInt(toQuarter.replace('Q', '')),
        }),
      designatedOperator: ppoValue === 'All' ? 'ALL' : ppoValue,
      inboundOrOutboundFilter: ppoFilter,
    };

    generateReport(params, {
      onSuccess: (response: CN10ReportResponse) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          console.log('Current filter params used:', params);
          
          // Check for 204 No Content or empty data
          if (response.noData || !response.data || (Array.isArray(response.data) && response.data.length === 0)) {
            // Set no data found state instead of clearing results
            setShowNoData(true);
            setHtmlResults(null);
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          if (response.data) {
            // Store the data in a variable to ensure type safety
            const newData: CN10DataStructure = response.data;
            // Clear no data found state and set new data
            setShowNoData(false);
            setHtmlResults(null);
            // Use setTimeout to ensure the state is cleared before setting new data
            setTimeout(() => {
              setHtmlResults(newData);
              console.log('Updated htmlResults state with new data:', newData);
            }, 0);
            // Apply current filters to SectionBar only when data is found
            applyFiltersToSectionBar();
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(
            `${format} Report downloaded successfully with filename: ${response.filename}`
          );
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        let errorMessage = 'An error occurred while generating the report';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message for all formats (HTML, PDF, XLS)
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No CN10 reports available') || errorMessage.includes('No CN10')) {
          showSnackbar(
            errorMessage || 'No CN10 reports available for the selected criteria',
            'warning'
          );
        } else {
          showSnackbar(errorMessage, 'error');
        }
        setShowNoData(false);
      },
    });
  };

  // No need for mailColumns anymore since we're using CN10Table

  // Initial URL parameter sync when page loads
  useEffect(() => {
    // Only sync once and when yearQuarterOptions are available
    if (hasSyncedUrlParams.current || yearQuarterOptions.length === 0) {
      return;
    }

    const ppoParam = searchParams.get('ppo');
    const typeParam = searchParams.get('type');
    const yearParam = searchParams.get('year');
    const mailCategoriesParam = searchParams.get('mailCategories');
    const fromPeriodParam = searchParams.get('fromPeriod');
    const toPeriodParam = searchParams.get('toPeriod');

    // Sync PPO parameter
    if (ppoParam) {
      setPpoValue(ppoParam === 'All' ? '' : ppoParam);
    }

    // Sync type/direction
    if (typeParam) {
      const mappedFilter =
        typeParam === 'inbound' ? 'Inbound' : typeParam === 'outbound' ? 'Outbound' : 'Inbound';
      setPpoFilter(mappedFilter);
    }

    // Sync year
    if (yearParam) {
      setFromYear(yearParam);
    }

    // Sync mail categories
    if (mailCategoriesParam) {
      const categories = mailCategoriesParam.split(',');
      setMailCategory({
        aircraftPriority: categories.includes('A'),
        surfacePriority: categories.includes('D'),
      });
    }

    // Sync from period
    if (fromPeriodParam) {
      const option = yearQuarterOptions.find((opt) => opt.value === fromPeriodParam);
      if (option) {
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    }

    // Sync to period
    if (toPeriodParam) {
      const option = yearQuarterOptions.find((opt) => opt.value === toPeriodParam);
      if (option) {
        setToYear(option.year);
                 setToQuarter(option.quarter);
       }
     }

     // Mark as synced
     hasSyncedUrlParams.current = true;
   }, [searchParams, yearQuarterOptions]); // Only depend on searchParams and yearQuarterOptions

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const typeParam = searchParams.get('type');
    const yearParam = searchParams.get('year');
    const mailCategoriesParam = searchParams.get('mailCategories');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults && !showNoData) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam === 'All' ? '' : ppoParam);
      }

      if (typeParam && typeParam !== ppoFilter.toLowerCase()) {
        const mappedFilter =
          typeParam === 'inbound' ? 'Inbound' : typeParam === 'outbound' ? 'Outbound' : 'Inbound';
        setPpoFilter(mappedFilter);
      }

      if (yearParam && yearParam !== 'all' && yearParam !== fromYear) {
        setFromYear(yearParam);
      }

      if (mailCategoriesParam) {
        const categories = mailCategoriesParam.split(',');
        const newMailCategory = {
          aircraftPriority: categories.includes('A'),
          surfacePriority: categories.includes('D'),
        };
        // Only update if the values are actually different
        if (newMailCategory.aircraftPriority !== mailCategory.aircraftPriority || 
            newMailCategory.surfacePriority !== mailCategory.surfacePriority) {
          setMailCategory(newMailCategory);
        }
      }
    }
  }, [searchParams, htmlResults, showNoData, ppoValue, ppoFilter, fromYear, mailCategory]);

  // Remove auto-regeneration - users must click export buttons manually

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setShowNoData(false);
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Helper functions to determine button text
  const getMailCategoryButtonText = () => {
    const allSelected = mailCategory.aircraftPriority && mailCategory.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarMailCategoriesChange = useCallback((values: string[]) => {
    setMailCategory({
      aircraftPriority: values.includes('A'),
      surfacePriority: values.includes('D'),
    });
    // Update URL parameters immediately when filter changes
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('mailCategories', values.join(','));
    } else {
      newParams.delete('mailCategories');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleSectionBarFromPeriodChange = useCallback(
    (period: string) => {
      if (period) {
        const option = yearQuarterOptions.find((opt) => opt.value === period);
        if (option) {
          setFromYear(option.year);
          setFromQuarter(option.quarter);
          // Update URL parameters immediately when filter changes
          const newParams = new URLSearchParams(searchParams);
          newParams.set('year', option.year);
          newParams.set('fromPeriod', period);
          setSearchParams(newParams);
        }
      } else {
        setFromYear('');
        setFromQuarter('');
        // Clear URL parameters when filter is cleared
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('year');
        newParams.delete('fromPeriod');
        setSearchParams(newParams);
      }
    },
    [yearQuarterOptions, searchParams, setSearchParams]
  );

  const handleSectionBarToPeriodChange = useCallback(
    (period: string) => {
      if (period) {
        const option = yearQuarterOptions.find((opt) => opt.value === period);
        if (option) {
          setToYear(option.year);
          setToQuarter(option.quarter);
          // Update URL parameters immediately when filter changes
          const newParams = new URLSearchParams(searchParams);
          newParams.set('toPeriod', period);
          setSearchParams(newParams);
        }
      } else {
        setToYear('');
        setToQuarter('');
        // Clear URL parameters when filter is cleared
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('toPeriod');
        setSearchParams(newParams);
      }
    },
    [yearQuarterOptions, searchParams, setSearchParams]
  );

  // Handler function for SectionBar type/direction change
  const handleSectionBarTypeChange = useCallback((type: string) => {
    const mappedType = type === 'inbound' ? 'Inbound' : 'Outbound';
    setPpoFilter(mappedType);
    // Update URL parameters immediately when filter changes
    const newParams = new URLSearchParams(searchParams);
    newParams.set('type', type);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results (not when no data found)
    if (!htmlResults || showNoData) return undefined;

    return {
      mailCategories: getMailCategoryValues(), // Use CN10-specific mailCategories field
      fromPeriod: getFromValue(),
      toPeriod: getToValue(),
      type: ppoFilter.toLowerCase(), // Convert to lowercase for SectionBar
      yearQuarterOptions: yearQuarterOptions, // Pass cascaded year-quarter options
      onMailCategoriesChange: handleSectionBarMailCategoriesChange,
      onFromPeriodChange: handleSectionBarFromPeriodChange,
      onToPeriodChange: handleSectionBarToPeriodChange,
      onTypeChange: handleSectionBarTypeChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    showNoData,
    mailCategory.aircraftPriority,
    mailCategory.surfacePriority,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    yearQuarterOptions,
    getMailCategoryValues,
    handleSectionBarMailCategoriesChange,
    handleSectionBarFromPeriodChange,
    handleSectionBarToPeriodChange,
    handleSectionBarTypeChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='cn10-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='CN10 Report'
            subtitle='e09 IBRS'
            module="e09"
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults && !showNoData,
              type: !!htmlResults && !showNoData,
              customCN10: !!htmlResults && !showNoData,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='cn10-report-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='cn10-report-main-content'>
            <ReportContainer data-testid='cn10-report-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='cn10-report-header'
              >
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 600, color: '#063142' }}
                  data-testid='cn10-report-title'
                >
                  CN10 Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid='cn10-report-header-actions'>
                  {(htmlResults || showNoData) && (
                    <ClearResultsButton
                      variant='outlined'
                      onClick={clearResults}
                      data-testid='cn10-report-clear-results-btn'
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection>
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                            >
                              <MenuItem value='All'>All</MenuItem>
                              {isPpoLoading && (
                                <MenuItem value='' disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading &&
                                !ppoError &&
                                ppoOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                      }}
                                    >
                                      <span>{option.label}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px',
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        mb: 2,
                      }}
                    />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          From YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => {
                                const option = yearQuarterOptions.find((opt) => opt.value === e.target.value);
                                if (option) {
                                  handleYearChange(option.year);
                                  handleFromChange(e.target.value);
                                }
                              }}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                  />

                  {/* Mail Category Section */}
                  <Box
                    sx={{
                      padding: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        width: '100%',
                        maxWidth: '400px',
                      }}
                    >
                      <SectionTitle>Mail Category</SectionTitle>
                      <Link
                        component='button'
                        onClick={handleSelectAllMailCategory}
                        sx={{
                          color: '#00ACE7',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: 500,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#E3F7FE',
                            color: '#0095C7',
                            textDecoration: 'none',
                          },
                          '&:active': {
                            backgroundColor: '#B3E5FC',
                          },
                        }}
                      >
                        {getMailCategoryButtonText()}
                      </Link>
                    </Box>
                    <CheckboxGroup sx={{ width: '100%', maxWidth: '400px' }}>
                      <CustomCheckbox
                        checked={mailCategory.aircraftPriority}
                        onChange={handleMailCategoryChange('aircraftPriority')}
                        label='A - Airmail Priority'
                      />
                      <CustomCheckbox
                        checked={mailCategory.surfacePriority}
                        onChange={handleMailCategoryChange('surfacePriority')}
                        label='D - Surface Priority'
                      />
                    </CheckboxGroup>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton variant='contained' onClick={handleReset}>
                      RESET
                    </ResetButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>

                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {showNoData && (
                <NoDataFound
                  title='No Data Available'
                  message='No data was found for the selected criteria. Please try adjusting your filters or selecting a different date range.'
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                  clearButtonText='Clear Results'
                  resetButtonText='Reset Filters'
                />
              )}

              {/* Results Section - Show tables directly under filters */}
              {htmlResults && <ResultsSection htmlResults={htmlResults} direction={ppoFilter} />}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="cn10-report-snackbar"
      />
    </Box>
  );
}

export default CN10Report;
